import { useState } from "react";
import Modal from "../../../components/modal/Modal";
import "./stats-settings.css";

type EntityType = "groups" | "users" | "apps" | "projects";

type SettingField = {
    label: string;
    key: string;
    type: "toggle" | "dropdown" | "input";
    options?: string[];
};

type SettingsConfig = {
    title: string;
    description: string;
    fields: SettingField[];
};

const settingsConfig: Record<EntityType, SettingsConfig> = {
    groups: {
        title: "Group Settings",
        description: "Manage how group stats are displayed.",
        fields: [
            { label: "Show inactive groups", key: "showInactive", type: "toggle" },
            { label: "Default sort", key: "defaultSort", type: "dropdown", options: ["Name", "Members", "Created"] },
            { label: "Groups per page", key: "perPage", type: "input" },
        ],
    },
    apps: {
        title: "App Settings",
        description: "Manage how app stats are displayed.",
        fields: [
            { label: "Show hidden apps", key: "showHidden", type: "toggle" },
            { label: "Default sort", key: "defaultSort", type: "dropdown", options: ["Name", "Usage", "Last Active"] },
            { label: "Apps per page", key: "perPage", type: "input" },
        ],
    },
    users: {
        title: "User Settings",
        description: "Manage how user stats are displayed.",
        fields: [
            { label: "Show inactive users", key: "showInactive", type: "toggle" },
            { label: "Default sort", key: "defaultSort", type: "dropdown", options: ["Name", "Activity", "Joined"] },
            { label: "Users per page", key: "perPage", type: "input" },
        ],
    },
    projects: {
        title: "Project Settings",
        description: "Manage how project stats are displayed.",
        fields: [
            { label: "Show completed projects", key: "showCompleted", type: "toggle" },
            { label: "Default sort", key: "defaultSort", type: "dropdown", options: ["Name", "Count", "Created"] },
            { label: "Projects per page", key: "perPage", type: "input" },
        ],
    },
};

type SettingsValues = Record<string, string | boolean>;

function defaultValues(config: SettingsConfig): SettingsValues {
    return Object.fromEntries(
        config.fields.map((field) => [
            field.key,
            field.type === "toggle" ? false : field.type === "dropdown" ? (field.options?.[0] ?? "") : "",
        ])
    );
}

function SettingFieldRenderer({
    field,
    value,
    onChange,
}: {
    field: SettingField;
    value: string | boolean;
    onChange: (key: string, value: string | boolean) => void;
}) {
    switch (field.type) {
        case "toggle":
            return (
                <div className="settings-field">
                    <span className="settings-field-label">{field.label}</span>
                    <label className="settings-switch">
                        <input
                            type="checkbox"
                            checked={Boolean(value)}
                            onChange={(e) => onChange(field.key, e.target.checked)}
                        />
                        <span className="settings-slider" />
                    </label>
                </div>
            );
        case "dropdown":
            return (
                <div className="settings-field">
                    <label htmlFor={`setting-${field.key}`}>{field.label}</label>
                    <select
                        id={`setting-${field.key}`}
                        className="settings-input"
                        value={String(value)}
                        onChange={(e) => onChange(field.key, e.target.value)}
                    >
                        {field.options?.map((opt) => (
                            <option key={opt} value={opt}>{opt}</option>
                        ))}
                    </select>
                </div>
            );
        case "input":
            return (
                <div className="settings-field">
                    <label htmlFor={`setting-${field.key}`}>{field.label}</label>
                    <input
                        id={`setting-${field.key}`}
                        className="settings-input"
                        type="text"
                        value={String(value)}
                        onChange={(e) => onChange(field.key, e.target.value)}
                    />
                </div>
            );
    }
}

type Props = {
    isOpen: boolean;
    onClose: () => void;
    entityType: EntityType;
};

export default function StatsSettings({ isOpen, onClose, entityType }: Props) {
    const config = settingsConfig[entityType];
    const [values, setValues] = useState<SettingsValues>(() => defaultValues(config));

    const handleChange = (key: string, value: string | boolean) => {
        setValues((prev) => ({ ...prev, [key]: value }));
    };

    const handleSave = () => {
        onClose();
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={config.title}>
            <div className="stats-settings-modal">
                <p className="settings-description">{config.description}</p>

                <div className="settings-fields">
                    {config.fields.map((field) => (
                        <SettingFieldRenderer
                            key={field.key}
                            field={field}
                            value={values[field.key]}
                            onChange={handleChange}
                        />
                    ))}
                </div>

                <button type="button" className="settings-save" onClick={handleSave}>
                    Save
                </button>
            </div>
        </Modal>
    );
}
